import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { apiAreas, httpMethods, safeRecentRequests } from "./apiRequestContext.js";

const workflows = new Set(["browser", "bootstrap", "auth", "draft_sync", "draft_commit", "excel_preview", "excel_apply", "analysis", "api_request"]);
const outcomes = new Set(["succeeded", "failed", "blocked", "cancelled", "unknown"]);
const reasons = new Set(["completed", "browser_error", "resource_load_failed", "unhandled_rejection", "render_failed", "bootstrap_failed", "auth_recovery_failed", "request_failed", "persistence_unknown", "import_partial_result", "missing_inputs", "model_terminal_failure", "telemetry_canary", "expected_422", "unexpected_422"]);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function newAttemptId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* Older/embedded browsers can restrict this API. */ }
  const bytes = new Uint8Array(16);
  try {
    if (!window.crypto?.getRandomValues) throw new Error("Crypto unavailable");
    window.crypto.getRandomValues(bytes);
  } catch {
    // Correlation only, never an authentication token. A fixed fallback UUID
    // would merge unrelated failures and silently suppress their alerts.
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function routeName(path = "") {
  if (/\/excel(?:\/|$)/.test(path)) return "excel";
  if (/\/dashboard(?:\/|$)/.test(path)) return "dashboard";
  if (/\/cropyear\//.test(path)) return "cropyear";
  if (/\/farm\//.test(path)) return "farm";
  if (/\/login(?:\/|$)/.test(path)) return "login";
  return "other";
}

// Only allow-listed, bounded classifications leave the browser. Raw exceptions,
// request/response bodies, URLs, account identities and workbook data never do.
export function createTelemetry({ connectionString, environment, release, enabled = true, synthetic = false, sdkFactory = config => new ApplicationInsights({ config }) } = {}) {
  let sdk = null;
  if (!enabled || !connectionString || !["staging", "production"].includes(environment)) return { report() {}, flush() {} };
  try {
    sdk = sdkFactory({ connectionString, disableExceptionTracking: true, disableAjaxTracking: true, disableFetchTracking: true, enableAutoRouteTracking: false, autoTrackPageVisitTime: false, isCookieUseDisabled: true, enableUnhandledPromiseRejectionTracking: false, samplingPercentage: 100 });
    sdk.addTelemetryInitializer(item => {
      if (item.baseType !== "EventData" || item.baseData?.name !== "ftmc.workflow.result") return false;
      // SDK context enrichment must not reintroduce URLs, cookies or identities.
      item.tags = { "ai.cloud.role": "ftmc-web" };
      item.ext = {};
      item.baseData.measurements = {};
      return true;
    });
    sdk.loadAppInsights();
  } catch { return { report() {}, flush() {} }; }
  const seen = new Set();
  return {
    report(input = {}) {
      if (!workflows.has(input.workflow) || !outcomes.has(input.outcome) || !reasons.has(input.reason)) return;
      const attempt = uuid.test(input.attemptId || "") ? input.attemptId : newAttemptId();
      const key = `${attempt}:${input.workflow}:${input.outcome}:${input.reason}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (seen.size > 500) seen.delete(seen.values().next().value);
      const properties = {
        environment, release: /^[a-zA-Z0-9._-]{1,64}$/.test(release || "") ? release : "unknown",
        role: "ftmc-web", workflow: input.workflow, outcome: input.outcome, reason: input.reason,
        attempt_id: attempt, request_id: uuid.test(input.requestId || "") ? input.requestId : "",
        route: routeName(input.path ?? window.location?.pathname), synthetic: String(Boolean(synthetic)),
        duration_bucket: input.durationMs > 30000 ? "over30s" : input.durationMs > 5000 ? "5to30s" : "under5s",
      };
      if (input.workflow === "auth") {
        const stages = new Set(["silent_token", "viewer_refresh", "interactive_recovery", "api_retry"]);
        const authReasons = new Set(["interaction_required", "offline", "network_unavailable", "interaction_cancelled", "access_denied", "retry_required"]);
        properties.auth_stage = stages.has(input.auth_stage) ? input.auth_stage : "silent_token";
        properties.auth_reason = authReasons.has(input.auth_reason) ? input.auth_reason : "retry_required";
        properties.auth_lane = ["external", "workforce"].includes(input.auth_lane) ? input.auth_lane : "unknown";
        properties.correlation_id = uuid.test(input.correlation_id || "") ? input.correlation_id : "";
      }
      if (input.workflow === "api_request" && input.http_status === 422) {
        properties.http_status = "422";
        properties.priority = input.reason === "unexpected_422" ? "S0" : "validation";
        properties.api_area = apiAreas.has(input.api_area) ? input.api_area : "other";
        properties.http_method = httpMethods.has(input.http_method) ? input.http_method : "GET";
        properties.server_release = /^[a-zA-Z0-9._-]{1,64}$/.test(input.server_release || "") ? input.server_release : "unknown";
        properties.recent_requests = safeRecentRequests(input.recent_requests);
      }
      for (const name of ["created", "updated", "unchanged", "skipped_error", "case_opened", "attention"]) {
        const count = input.counts?.[name];
        if (Number.isInteger(count) && count >= 0 && count <= 100000) properties[name] = String(count);
      }
      try {
        sdk.trackEvent({ name: "ftmc.workflow.result" }, properties);
        // Fatal UI failures may immediately lead to a reload or a closed tab.
        // Start delivery now rather than waiting for the normal SDK batch timer.
        if ((["browser", "bootstrap"].includes(input.workflow) && input.outcome === "failed") || properties.priority === "S0") sdk.flush();
      } catch { /* Collection is fail-open. */ }
    },
    flush() { try { sdk.flush(); } catch { /* Saving must not depend on telemetry. */ } },
  };
}

let collector = { report() {}, flush() {} };
export function initTelemetry() {
  collector = createTelemetry({
    connectionString: process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING,
    environment: process.env.REACT_APP_TELEMETRY_ENVIRONMENT,
    release: process.env.REACT_APP_RELEASE,
    enabled: process.env.REACT_APP_E2E_AUTH_BYPASS !== "true" || process.env.REACT_APP_TELEMETRY_TEST_ENABLED === "true",
    synthetic: process.env.REACT_APP_TELEMETRY_TEST_ENABLED === "true",
  });
  window.addEventListener("error", event => reportWorkflow({workflow:"browser", outcome:"failed", reason:event.error ? "browser_error" : "resource_load_failed"}), true);
  window.addEventListener("unhandledrejection", () => reportWorkflow({workflow:"browser", outcome:"failed", reason:"unhandled_rejection"}));
}
export const reportWorkflow = input => collector.report(input);
export const flushTelemetry = () => collector.flush();
